/**
 * Tool Call Display Utilities
 * Enhanced display of tool calls and results for better UX
 */

import chalk from 'chalk';
import { PAEAN_SYMBOLS } from './theme/branding.js';

/**
 * Tool call information
 */
export interface ToolCallInfo {
    id: string;
    name: string;
    type: 'local' | 'mcp' | 'system';
    serverName?: string;
    args?: Record<string, unknown>;
    status: 'pending' | 'running' | 'completed' | 'error';
    startTime?: number;
    endTime?: number;
    result?: unknown;
    error?: string;
}

/**
 * Tool category mapping for display icons
 */
const TOOL_CATEGORIES: Record<string, { icon: string; color: (s: string) => string }> = {
    // Coding tools (new)
    paean_edit_file: { icon: '✎', color: chalk.green },
    paean_grep: { icon: '⌕', color: chalk.yellow },
    paean_glob: { icon: '◎', color: chalk.cyan },
    paean_read_many_files: { icon: '⊞', color: chalk.blue },
    paean_web_fetch: { icon: '⇣', color: chalk.magenta },
    paean_memory: { icon: '◆', color: chalk.magenta },
    // File operations
    paean_read_file: { icon: '▸', color: chalk.blue },
    paean_write_file: { icon: '✏', color: chalk.green },
    paean_list_directory: { icon: '▤', color: chalk.blue },
    paean_download_file: { icon: '⇣', color: chalk.cyan },
    // Shell/System tools
    paean_execute_shell: { icon: '⌘', color: chalk.yellow },
    paean_check_process: { icon: '◉', color: chalk.cyan },
    paean_kill_process: { icon: '✕', color: chalk.red },
    // CLI agent tools
    paean_invoke_cli_agent: { icon: '∩', color: chalk.magenta },
    paean_list_cli_agents: { icon: '≡', color: chalk.cyan },
    paean_setup_a8e: { icon: '⚡', color: chalk.green },
    // Worker tools
    paean_worker_execute_prompt: { icon: '∩', color: chalk.magenta },
    paean_worker_start: { icon: '▶', color: chalk.green },
    paean_worker_stop: { icon: '■', color: chalk.red },
    paean_worker_status: { icon: '◉', color: chalk.cyan },
    paean_worker_query: { icon: '?', color: chalk.blue },
    paean_worker_submit: { icon: '➤', color: chalk.green },
    // Cloud tools
    generateSparklet: { icon: '✨', color: chalk.magenta },
    updateSparklet: { icon: '✨', color: chalk.magenta },
    generateImage: { icon: '⬡', color: chalk.cyan },
    generateVideo: { icon: '⬡', color: chalk.cyan },
    // Default
    default: { icon: '⚙', color: chalk.gray },
};

/**
 * Get tool display info
 */
function getToolDisplay(toolName: string): { icon: string; color: (s: string) => string } {
    return TOOL_CATEGORIES[toolName] || TOOL_CATEGORIES.default;
}

/**
 * Format tool arguments for display
 */
function formatArgs(args?: Record<string, unknown>, maxLength = 50): string {
    if (!args || Object.keys(args).length === 0) return '';

    const entries = Object.entries(args);
    const formatted: string[] = [];

    for (const [key, value] of entries.slice(0, 3)) { // Show max 3 args
        let valueStr: string;
        if (typeof value === 'string') {
            valueStr = value.length > 20 ? value.slice(0, 17) + '...' : value;
        } else if (typeof value === 'boolean' || typeof value === 'number') {
            valueStr = String(value);
        } else if (Array.isArray(value)) {
            valueStr = `[${value.length} items]`;
        } else if (typeof value === 'object' && value !== null) {
            valueStr = '{...}';
        } else {
            valueStr = String(value);
        }
        formatted.push(`${key}=${valueStr}`);
    }

    const result = formatted.join(', ');
    if (result.length > maxLength) {
        return result.slice(0, maxLength - 3) + '...';
    }
    if (entries.length > 3) {
        return result + `, +${entries.length - 3} more`;
    }
    return result;
}

/**
 * Format duration for display
 */
function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

/**
 * Display tool call start
 */
export function displayToolCallStart(tool: ToolCallInfo): void {
    const { icon, color } = getToolDisplay(tool.name);
    const typeLabel = tool.type === 'mcp' ? `[MCP:${tool.serverName}] ` :
        tool.type === 'system' ? '[SYS] ' : '';

    const argsStr = formatArgs(tool.args);
    const argsDisplay = argsStr ? chalk.dim(` (${argsStr})`) : '';

    console.log(
        color(icon) + ' ' +
        chalk.dim(typeLabel) +
        chalk.bold(tool.name) +
        argsDisplay +
        chalk.dim(' ...')
    );
}

/**
 * Display tool call result with rich formatting for coding tools
 */
export function displayToolCallResult(tool: ToolCallInfo): void {
    const { icon, color } = getToolDisplay(tool.name);
    const duration = tool.endTime && tool.startTime
        ? formatDuration(tool.endTime - tool.startTime)
        : '';

    if (tool.status === 'error') {
        console.log(
            chalk.red('✗') + ' ' +
            chalk.dim(tool.name) + ' ' +
            chalk.red('failed') +
            (tool.error ? chalk.dim(` - ${tool.error.slice(0, 60)}`) : '') +
            (duration ? chalk.dim(` [${duration}]`) : '')
        );
    } else if (tool.status === 'completed') {
        const res = tool.result as Record<string, unknown> | undefined;

        // Tool-specific rich result formatting
        if (tool.name === 'paean_edit_file' && res?.success && res.diff) {
            const stats = res.stats as { linesAdded?: number; linesRemoved?: number } | undefined;
            const shortPath = (res.filePath as string || '').replace(process.cwd() + '/', '');
            console.log(
                color(icon) + ' ' +
                chalk.bold(shortPath) + ' ' +
                chalk.green(`+${stats?.linesAdded || 0}`) +
                chalk.red(` -${stats?.linesRemoved || 0}`) +
                (duration ? chalk.dim(` [${duration}]`) : '')
            );
            // Show compact diff
            const diffLines = (res.diff as string).split('\n');
            const maxDiffLines = 12;
            let shown = 0;
            for (const line of diffLines) {
                if (shown >= maxDiffLines) {
                    console.log(chalk.dim(`  ... ${diffLines.length - shown} more lines`));
                    break;
                }
                if (line.startsWith('+') && !line.startsWith('+++')) {
                    console.log(chalk.green(`  ${line}`));
                    shown++;
                } else if (line.startsWith('-') && !line.startsWith('---')) {
                    console.log(chalk.red(`  ${line}`));
                    shown++;
                } else if (line.startsWith('@@')) {
                    console.log(chalk.cyan(`  ${line}`));
                    shown++;
                }
            }
        } else if (tool.name === 'paean_grep' && res?.success) {
            const matchCount = (res.matchCount as number) || 0;
            const engine = res.engine as string || 'node';
            console.log(
                color(icon) + ' ' +
                chalk.dim('grep') + ' ' +
                chalk.bold(`${matchCount} match${matchCount !== 1 ? 'es' : ''}`) +
                chalk.dim(` (${engine})`) +
                (duration ? chalk.dim(` [${duration}]`) : '')
            );
        } else if (tool.name === 'paean_glob' && res?.success) {
            const fileCount = (res.fileCount as number) || 0;
            console.log(
                color(icon) + ' ' +
                chalk.dim('glob') + ' ' +
                chalk.bold(`${fileCount} file${fileCount !== 1 ? 's' : ''}`) +
                (duration ? chalk.dim(` [${duration}]`) : '')
            );
        } else if (tool.name === 'paean_read_many_files' && res?.success) {
            const filesRead = (res.filesRead as number) || 0;
            const totalLines = (res.totalLinesRead as number) || 0;
            console.log(
                color(icon) + ' ' +
                chalk.dim('read') + ' ' +
                chalk.bold(`${filesRead} file${filesRead !== 1 ? 's' : ''}`) +
                chalk.dim(` (${totalLines} lines)`) +
                (duration ? chalk.dim(` [${duration}]`) : '')
            );
        } else if (tool.name === 'paean_web_fetch' && res?.success) {
            const url = res.url as string || '';
            const shortUrl = url.length > 40 ? url.slice(0, 37) + '...' : url;
            console.log(
                color(icon) + ' ' +
                chalk.dim('fetch') + ' ' +
                chalk.underline(shortUrl) +
                (duration ? chalk.dim(` [${duration}]`) : '')
            );
        } else {
            // Generic result
            let resultPreview = '';
            if (res) {
                if ('success' in res) {
                    resultPreview = res.success ? chalk.green('success') : chalk.red('failed');
                } else if ('output' in res && typeof res.output === 'string') {
                    const lines = (res.output as string).split('\n').length;
                    resultPreview = chalk.dim(`(${lines} lines)`);
                }
            }

            console.log(
                color(icon) + ' ' +
                chalk.dim(tool.name) + ' ' +
                chalk.green(PAEAN_SYMBOLS.bullet) +
                (resultPreview ? ' ' + resultPreview : '') +
                (duration ? chalk.dim(` [${duration}]`) : '')
            );
        }
    }
}

/**
 * Display tool call summary (for batch operations)
 */
export function displayToolCallSummary(tools: ToolCallInfo[]): void {
    if (tools.length === 0) return;

    const completed = tools.filter(t => t.status === 'completed').length;
    const failed = tools.filter(t => t.status === 'error').length;

    const totalDuration = tools.reduce((sum, t) => {
        if (t.startTime && t.endTime) {
            return sum + (t.endTime - t.startTime);
        }
        return sum;
    }, 0);

    console.log('');
    console.log(
        chalk.dim('──') + ' ' +
        chalk.bold('Tool Summary: ') +
        chalk.green(`${completed} completed`) +
        (failed > 0 ? chalk.red(`, ${failed} failed`) : '') +
        chalk.dim(` in ${formatDuration(totalDuration)}`)
    );
}

/**
 * Create a tool call tracker for collecting tool calls
 */
export function createToolCallTracker() {
    const calls: Map<string, ToolCallInfo> = new Map();

    return {
        start(id: string, name: string, type: 'local' | 'mcp' | 'system', args?: Record<string, unknown>, serverName?: string) {
            const tool: ToolCallInfo = {
                id,
                name,
                type,
                serverName,
                args,
                status: 'running',
                startTime: Date.now(),
            };
            calls.set(id, tool);
            displayToolCallStart(tool);
        },

        complete(id: string, result?: unknown) {
            const tool = calls.get(id);
            if (tool) {
                tool.status = 'completed';
                tool.endTime = Date.now();
                tool.result = result;
                displayToolCallResult(tool);
            }
        },

        error(id: string, error: string) {
            const tool = calls.get(id);
            if (tool) {
                tool.status = 'error';
                tool.endTime = Date.now();
                tool.error = error;
                displayToolCallResult(tool);
            }
        },

        getAll(): ToolCallInfo[] {
            return Array.from(calls.values());
        },

        showSummary() {
            displayToolCallSummary(Array.from(calls.values()));
        },

        clear() {
            calls.clear();
        }
    };
}

/**
 * Format tool call for inline display (single line)
 */
export function formatToolCallInline(name: string, status: 'pending' | 'running' | 'completed' | 'error'): string {
    const { icon, color } = getToolDisplay(name);

    switch (status) {
        case 'pending':
            return chalk.dim(`${icon} ${name} ...`);
        case 'running':
            return color(`${icon} ${name}`) + chalk.dim(' running...');
        case 'completed':
            return color(`${icon} ${name}`) + chalk.green(' ✓');
        case 'error':
            return chalk.red(`${icon} ${name} ✗`);
    }
}

/**
 * Export display functions for use in renderer
 */
export const toolDisplay = {
    start: displayToolCallStart,
    result: displayToolCallResult,
    summary: displayToolCallSummary,
    createTracker: createToolCallTracker,
    formatInline: formatToolCallInline,
};
